import React from "react";
import { motion } from "framer-motion";
import { Lightbulb, User, Feather, ShieldCheck } from "lucide-react";

/**
 * AgentGraph
 * Renders the four agents (Idea, Persona, Writing, Quality) as a "sacred geometry" node graph.
 * Each node reports one of: idle | running | success | error, with optional latency + tokens.
 *
 * Layout: diamond
 *      Idea
 *     /    \
 * Persona  Writing
 *     \    /
 *     Quality
 * The graph is presented left-to-right for the sequential flow: Idea -> Persona -> Writing -> Quality.
 */

const AGENTS = [
    { key: "idea", label: "Idea", icon: Lightbulb, subtitle: "Understand intent" },
    { key: "persona", label: "Persona", icon: User, subtitle: "Personalize voice" },
    { key: "writing", label: "Writing", icon: Feather, subtitle: "Draft the post" },
    { key: "quality", label: "Quality", icon: ShieldCheck, subtitle: "Polish + audit" },
];

function StatusDot({ status }) {
    const color = {
        idle: "bg-[var(--lk-border)]",
        running: "bg-[var(--lk-primary)] lk-pulse",
        success: "bg-emerald-400",
        error: "bg-red-400",
    }[status] || "bg-[var(--lk-border)]";
    return <span className={`inline-block w-2 h-2 rounded-full ${color}`} />;
}

function AgentNode({ agent, state, index }) {
    const Icon = agent.icon;
    const isActive = state?.status === "running";
    const isDone = state?.status === "success";
    const isErr = state?.status === "error";
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * index, duration: 0.4 }}
            className={`relative flex-1 min-w-[180px] rounded-xl p-5 lk-border lk-surface-2 ${
                isActive ? "lk-glow" : ""
            } ${isErr ? "border-red-500/60" : ""}`}
            data-testid={`agent-node-${agent.key}`}
        >
            <div className="flex items-center justify-between">
                <div className="lk-label">Agent {index + 1}</div>
                <StatusDot status={state?.status || "idle"} />
            </div>
            <div className="mt-3 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${
                    isActive || isDone ? "border-[var(--lk-primary)]" : "border-[var(--lk-border)]"
                }`}>
                    <Icon className={`w-5 h-5 ${isActive || isDone ? "text-[var(--lk-primary)]" : "text-[var(--lk-muted)]"}`} />
                </div>
                <div>
                    <div className="font-heading text-2xl leading-none">{agent.label}</div>
                    <div className="text-xs text-[var(--lk-muted)] mt-1">{agent.subtitle}</div>
                </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div className="flex flex-col">
                    <span className="lk-label">Latency</span>
                    <span className="font-mono text-[var(--lk-text)]" data-testid={`agent-latency-${agent.key}`}>
                        {state?.latency_ms ? `${Math.round(state.latency_ms)} ms` : "—"}
                    </span>
                </div>
                <div className="flex flex-col">
                    <span className="lk-label">Tokens</span>
                    <span className="font-mono text-[var(--lk-text)]" data-testid={`agent-tokens-${agent.key}`}>
                        {state?.tokens || "—"}
                    </span>
                </div>
            </div>

            {state?.model ? (
                <div className="mt-3 text-[0.65rem] text-[var(--lk-muted)] truncate font-mono">
                    {state.provider}/{state.model}
                </div>
            ) : null}
        </motion.div>
    );
}

export default function AgentGraph({ traces = [], running = false, currentIndex = -1 }) {
    // Map traces by agent name
    const byKey = Object.fromEntries((traces || []).map((t) => [t.agent_name, t]));

    return (
        <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {AGENTS.map((a, i) => {
                    const trace = byKey[a.key];
                    let status = "idle";
                    if (trace) status = trace.status === "error" ? "error" : "success";
                    else if (running && i === currentIndex) status = "running";
                    return (
                        <AgentNode
                            key={a.key}
                            agent={a}
                            index={i}
                            state={{
                                status,
                                latency_ms: trace?.latency_ms,
                                tokens: trace?.tokens,
                                provider: trace?.provider,
                                model: trace?.model,
                            }}
                        />
                    );
                })}
            </div>

            {/* Connecting line SVG (decorative) */}
            <svg
                className="hidden md:block absolute left-0 right-0 top-1/2 -translate-y-1/2 pointer-events-none"
                height="24"
                width="100%"
                aria-hidden="true"
            >
                <line
                    x1="10%"
                    x2="90%"
                    y1="12"
                    y2="12"
                    stroke="var(--lk-primary)"
                    strokeOpacity="0.35"
                    strokeWidth="1"
                    className="lk-dash"
                />
            </svg>
        </div>
    );
}
